import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  NgModule,
  OnInit,
  Output,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute } from '@angular/router';
import { SearchEngineFieldCheckbox } from '@domains/search-engine/entities/checkbox-search-field';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-checkbox-search',
  templateUrl: './checkbox-search.component.html',
  styleUrls: ['./checkbox-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxSearchComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxSearchComponent implements ControlValueAccessor, OnInit {
  @Input() field!: SearchEngineFieldCheckbox;
  @Input() modalOpen!: boolean;
  @Input() liveSearch!: boolean;

  @Output() hasChanged = new EventEmitter<boolean>();

  checkboxForm!: FormGroup;

  private _value = false;

  set value(value: boolean) {
    if (this._value === value) {
      return;
    }
    this._value = value;
    this.checkboxForm?.get(this.field.name)?.setValue(value);
    this.onChanged(value);
    this.onTouched();
  }

  get value(): boolean {
    return this._value;
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap.pipe(take(1)).subscribe({
      next: (q) =>
        (this.checkboxForm = this.formBuilder.group({
          [this.field.name]: [q.get(this.field.name)],
        })),
    });

    this.checkboxForm
      ?.get(this.field.name)
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (value) => {
          this.value = value;
          this.hasChanged.emit();
        },
      });
  }

  onChanged: (_: boolean) => void = (_: boolean) => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChanged = (value) => {
      fn(value);
      this.cdr.markForCheck();
    };
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  writeValue = (value: boolean): boolean => (this.value = value);

  selectValue(event?: any): void {
    if (event) {
      event.preventDefault();
    }
    this.checkboxForm?.get(this.field.name)?.setValue(!this.value);
  }
}
@NgModule({
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatInputModule],
  declarations: [CheckboxSearchComponent],
  exports: [CheckboxSearchComponent],
})
export class SearchEngineCheckboxSearchModule {}
